// Legacy CRM Client - Updated to use new modular API structure
// This file maintains backward compatibility while using the new API services

import { 
  authApi, 
  dealsApi, 
  tasksApi, 
  accountsApi, 
  forecastsApi, 
  insightsApi, 
  searchApi,
  documentsApi,
  baseClient 
} from './index';

// Legacy CRMClient class for backward compatibility
class CRMClient {
  private token: string | null = null;
  public defaults = {
    baseURL: import.meta.env.VITE_API_URL || ''
  };

  setToken(token: string | null) {
    this.token = token;
    if (token) {
      authApi.setToken(token);
    }
  }

  // Auth endpoints
  async login(email: string, password: string) {
    const response = await authApi.login(email, password);
    return response;
  }

  // Deals endpoints
  async getDeals() {
    return dealsApi.getDeals();
  }

  async getDeal(id: string) {
    return dealsApi.getDeal(id);
  }

  async createDeal(data: any) {
    return dealsApi.createDeal(data);
  }

  async updateDeal(id: string, data: any) {
    return dealsApi.updateDeal(id, data);
  }

  // Deal Review Queue endpoint
  async getDealReviewQueue() {
    return insightsApi.getDealReviewQueue();
  }

  // Forecast endpoints
  async getLatestForecast() {
    return forecastsApi.getLatestForecast();
  }

  async getForecastDetails(forecastId: string) {
    return forecastsApi.getForecastDetails(forecastId);
  }

  // Tasks endpoints
  async getTasks() {
    return tasksApi.getTasks();
  }

  async createTask(data: any) {
    return tasksApi.createTask(data);
  }

  async updateTask(id: string, data: any) {
    return tasksApi.updateTask(id, data);
  }

  // Accounts endpoints
  async getAccounts() {
    return accountsApi.getAccounts();
  }

  async getAccount(id: string) {
    return accountsApi.getAccount(id);
  }

  // Workflows endpoints
  async getWorkflows() {
    return dealsApi.getWorkflows();
  }

  async createWorkflow(data: any) {
    return dealsApi.createWorkflow(data);
  }

  async updateWorkflow(id: string, data: any) {
    return dealsApi.updateWorkflow(id, data);
  }

  // Documents endpoints
  async getDocuments(dealId?: string) {
    if (dealId) {
      return documentsApi.getDocumentsByDeal(dealId);
    }
    return documentsApi.getDocuments();
  }

  async uploadDocument(file: File, dealId?: string) {
    return documentsApi.uploadDocument(file, dealId);
  }

  // Search endpoint
  async search(query: string) {
    return searchApi.globalSearch({ q: query });
  }
}

// Export the functions needed by components (maintaining backward compatibility)
export const crmClient = new CRMClient();

// Legacy function exports for backward compatibility
export const getDealReviewQueue = () => crmClient.getDealReviewQueue();
export const getLatestForecast = () => crmClient.getLatestForecast();
export const getForecastDetails = (forecastId: string) => crmClient.getForecastDetails(forecastId);

// Export the new modular APIs for new components
export {
  authApi,
  dealsApi,
  tasksApi,
  accountsApi,
  contactsApi,
  activitiesApi,
  workflowsApi,
  forecastsApi,
  insightsApi,
  searchApi,
  dashboardApi,
  documentsApi,
  performanceApi,
  portalApi,
  summariesApi,
  integrationsApi,
  baseClient
} from './index';

// Export types
export * from '../types/api';